import { Routes, Route, useLocation } from "react-router-dom";
import Sidebar from "./scenes/global/Sidebar";
import { CssBaseline, ThemeProvider, Box, CircularProgress } from "@mui/material";
import { SnackbarProvider } from 'notistack';
import { ColorModeContext, useMode } from "./theme";
import { SidebarProvider } from "./context/SidebarContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import { LicenseProvider } from "./contexts/LicenseContext";
import { FeatureProvider } from "./contexts/FeatureContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { TimeRangeProvider } from "./contexts/TimeRangeContext";
import { RetentionProvider } from "./contexts/RetentionContext";
import { TimezoneProvider } from "./timezone";
import { SyntheticsWebSocketProvider } from "./contexts/SyntheticsWebSocketContext";
import { TourProvider } from "./components/tour/TourContext";
import ProductTour from "./components/tour/ProductTour";
import { DeploymentWebSocketProvider } from "./contexts/DeploymentWebSocketContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PasswordWarningBanner from "./components/PasswordWarningBanner";
import LicenseWarningBanner from "./components/LicenseWarningBanner";
import AppRoutes from "./components/AppRoutes";
import ThemeAuthBridge from "./components/ThemeAuthBridge";
import SidebarAuthBridge from "./components/SidebarAuthBridge";
import ContextChooser from "./scenes/admin/ContextChooser";
import OperatorLayout from "./scenes/admin/OperatorLayout";
import LandingPage from "./scenes/marketing/LandingPage";
import SignupPage from "./scenes/marketing/SignupPage";
import SignupSuccessPage from "./scenes/marketing/SignupSuccessPage";
import TermsOfServicePage from "./scenes/marketing/TermsOfServicePage";
import PermalinkViewer from "./scenes/dashboard/PermalinkViewer";
import { isMarketingMode, isAppMode } from "./utils/deploymentMode";

/**
 * Inner app component that can use useLocation() (must be inside Router).
 * Routes /choose-context and /operator/* to their own layouts,
 * everything else to the standard Sidebar + AppRoutes layout.
 */
function AppContent() {
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const isOperatorRoute = location.pathname.startsWith('/operator');
  const isContextChooser = location.pathname === '/choose-context';
  const isRoot = location.pathname === '/';
  const isDocsRoute = location.pathname === '/docs' || location.pathname.startsWith('/docs/');
  const isSignupRoute = location.pathname === '/signup' || location.pathname.startsWith('/signup/');
  const isTermsRoute = location.pathname === '/terms';

  // Public documentation surface — works on both the marketing host
  // and the app host, without auth, so a user can land on a /docs/...
  // URL from anywhere (Slack, Google search, in-app help link) and
  // read the article without first signing in.
  //
  // Route shapes:
  //   /docs                                 → home (operator-flagged
  //                                            article OR landing card grid)
  //   /docs/:firstSegment                   → resolver: category landing
  //                                            if segment matches a slug,
  //                                            else flat article page
  //   /docs/:categorySlug/:articleSlug      → canonical article URL
  // Product docs are RETIRED — all documentation lives on the public site
  // (verops.io/docs, migrated 2026-08-08). Any /docs deep link redirects
  // there (nginx also 301s server-side; this covers SPA-internal hits).
  if (isDocsRoute) {
    window.location.replace('https://verops.io/docs');
    return null;
  }

  // Public signup — works on both marketing and app hosts. The page
  // itself fetches /api/v1.0/signup/config and renders an
  // "unavailable" state when the operator's master flag is off, so
  // a stale link doesn't dead-end the visitor.
  if (isSignupRoute) {
    return (
      <Routes>
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signup/success" element={<SignupSuccessPage />} />
      </Routes>
    );
  }

  // Public Terms of Service — works on both marketing and app hosts, opened in
  // its own window from the footer and the signup consent line.
  if (isTermsRoute) {
    return (
      <Routes>
        <Route path="/terms" element={<TermsOfServicePage />} />
      </Routes>
    );
  }

  // Marketing-host short-circuit: if the operator deployed this build to
  // {@code domain.com} (a non-{@code app.} hostname), they only ever see
  // the landing page. The platform tree (auth, dashboards, settings…)
  // doesn't render here — that lives at {@code app.domain.com}.
  if (isMarketingMode()) {
    return (
      <Routes>
        <Route path="*" element={<LandingPage />} />
      </Routes>
    );
  }

  // Single-host fallback ("dual" mode) — root path renders the landing
  // page when unauthenticated and the home page when authenticated. This
  // is what the dev domain still does.
  if (isRoot) {
    if (authLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress size={60} />
        </Box>
      );
    }
    if (!isAuthenticated && !isAppMode()) {
      return <LandingPage />;
    }
  }

  // Context chooser — full screen, no sidebar
  if (isContextChooser) {
    return (
      <ProtectedRoute superAdminRequired>
        <ContextChooser />
      </ProtectedRoute>
    );
  }

  // Operator panel — separate layout with OperatorSidebar.
  // SUPER_ADMIN gets the full panel; WEB_ADMIN ("Website Operator")
  // gets the same chrome but only the Website Controls section. The
  // sidebar handles role-based trimming; backend @PreAuthorize is the
  // authoritative gate on per-endpoint access.
  if (isOperatorRoute) {
    return (
      <ProtectedRoute operatorRequired>
        <DeploymentWebSocketProvider>
          <Routes>
            <Route path="/operator/*" element={<OperatorLayout />} />
          </Routes>
        </DeploymentWebSocketProvider>
      </ProtectedRoute>
    );
  }

  // Standard platform layout
  return (
    <ProtectedRoute>
      <PasswordWarningBanner />
      <LicenseWarningBanner />
      {/* TimezoneProvider lives inside ProtectedRoute so we only hit
          /api/profile after auth resolves. authVersion = isAuthenticated
          flips false→true on login → provider re-reads the new user's
          preferences without a page refresh. */}
      <TimezoneProvider authVersion={isAuthenticated ? 'in' : 'out'}>
      <RetentionProvider>
      <TimeRangeProvider>
        <SyntheticsWebSocketProvider>
        <DeploymentWebSocketProvider>
          <SidebarProvider>
            <SidebarAuthBridge>
              <TourProvider>
              <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
                <Sidebar />
                <Box component="main" sx={{ flex: 1, overflow: 'auto' }}>
                  <Routes>
                    <Route path="*" element={<AppRoutes />} />
                  </Routes>
                </Box>
              </Box>
              <ProductTour />
              </TourProvider>
            </SidebarAuthBridge>
          </SidebarProvider>
        </DeploymentWebSocketProvider>
        </SyntheticsWebSocketProvider>
      </TimeRangeProvider>
      </RetentionProvider>
      </TimezoneProvider>
    </ProtectedRoute>
  );
}

function App() {
  const [theme, colorMode] = useMode("light");

  // Marketing-host short-circuit: skip the entire auth tree. Public
  // surfaces (landing, docs, signup, signup-success) render here
  // without loading the auth/license/dashboards stack — that lives at
  // {@code app.domain.com}. Permalinks stay app-side (the kiosk URL
  // is {@code app.domain.com/p/:token}).
  if (isMarketingMode()) {
    return (
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider maxSnack={3}>
            <Routes>
              {/* Docs retired — everything lives on verops.io/docs now */}
              {/* Public signup surface */}
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/signup/success" element={<SignupSuccessPage />} />
              {/* Public Terms of Service */}
              <Route path="/terms" element={<TermsOfServicePage />} />
              {/* Catch-all → landing */}
              <Route path="*" element={<LandingPage />} />
            </Routes>
          </SnackbarProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    );
  }

  // Kiosk permalink path — strip the Auth/License/Organization providers so
  // a TV pinned at {@code /p/:token} never fires an apiClient 401 that bounces
  // it back to {@code /}. The viewer talks to no-auth endpoints.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/p/')) {
    return (
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Routes>
            <Route path="/p/:token" element={<PermalinkViewer />} />
          </Routes>
        </ThemeProvider>
      </ColorModeContext.Provider>
    );
  }

  return (
    <AuthProvider>
      <OrganizationProvider>
      <LicenseProvider>
      <FeatureProvider>
        <ColorModeContext.Provider value={colorMode}>
          <ThemeAuthBridge>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <SnackbarProvider
              maxSnack={3}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              autoHideDuration={3000}
            >
              <NotificationProvider>
                <AppContent />
              </NotificationProvider>
            </SnackbarProvider>
          </ThemeProvider>
          </ThemeAuthBridge>
        </ColorModeContext.Provider>
      </FeatureProvider>
      </LicenseProvider>
      </OrganizationProvider>
    </AuthProvider>
  );
}

export default App;
